import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../services/api';
import { 
  LayoutDashboard, 
  DoorOpen, 
  Calendar, 
  Users, 
  FileText, 
  ClipboardList,
  Settings,
  LogOut,
  ChevronRight,
  BookOpen
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { SmtpHealthBanner } from './SmtpHealthBanner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f048c5ac-5226-4423-b21a-02c530902854/artifacts/xnmh0jmc_Design%20sans%20titre%20%282%29.png";

export const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isAdmin = user?.role === 'admin';
  const isGestionnaire = user?.role === 'gestionnaire';
  const isUser = user?.role === 'utilisateur';

  // Define nav items based on role
  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: isUser ? 'Mon espace' : 'Tableau de bord', visible: true },
    { path: '/rooms', icon: DoorOpen, label: 'Salles', visible: !isUser },
    { path: '/calendar', icon: Calendar, label: 'Calendrier', visible: true },
    { path: '/reservations', icon: ClipboardList, label: 'Réservations', visible: true },
    { path: '/clients', icon: Users, label: 'Bénéficiaires', visible: !isUser },
    { path: '/billing', icon: FileText, label: 'Contributions', visible: true },
    { path: '/procedures', icon: BookOpen, label: 'Procédures', visible: true },
    { path: '/settings', icon: Settings, label: 'Paramètres', visible: true },
  ].filter(item => item.visible);

  return (
    <aside 
      className="fixed left-0 top-0 h-screen w-64 bg-[#1e3a8a] text-white flex flex-col z-50"
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="p-6 flex items-center gap-3">
        <img 
          src={LOGO_URL} 
          alt="Diocèse de Pamiers" 
          className="h-12 w-12 rounded-full bg-white p-1"
          data-testid="sidebar-logo"
        />
        <div>
          <h1 className="font-bold text-sm" style={{ fontFamily: 'Merriweather, serif' }}>
            Diocèse de Pamiers
          </h1>
          <p className="text-xs text-white/70">Gestion des Salles</p>
        </div>
      </div>

      <Separator className="bg-white/20" />

      {/* Navigation */}
      <ScrollArea className="flex-1 py-4">
        <nav className="px-3 space-y-1">
          <TooltipProvider>
            {navItems.map((item) => (
              <Tooltip key={item.path}>
                <TooltipTrigger asChild>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `sidebar-link ${isActive ? 'active' : ''}`
                    }
                    data-testid={`nav-${item.path.replace('/', '')}`}
                  >
                    <item.icon size={20} />
                    <span className="flex-1">{item.label}</span>
                    <ChevronRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </NavLink>
                </TooltipTrigger>
                <TooltipContent side="right" className="bg-slate-800 text-white">
                  {item.label}
                </TooltipContent>
              </Tooltip>
            ))}
          </TooltipProvider>
        </nav>
      </ScrollArea>

      <Separator className="bg-white/20" />

      {/* User section */}
      <div className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-10 w-10 rounded-full bg-[#c5a059] flex items-center justify-center font-medium">
            {user?.first_name?.[0]}{user?.last_name?.[0]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">
              {user?.first_name} {user?.last_name}
            </p>
            <p className="text-xs text-white/70 capitalize truncate">
              {user?.role === 'admin' ? 'Administrateur' : 
               user?.role === 'gestionnaire' ? 'Gestionnaire' : 'Utilisateur'}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          onClick={handleLogout}
          className="w-full justify-start text-white/80 hover:text-white hover:bg-white/10"
          data-testid="logout-button"
        >
          <LogOut size={18} className="mr-2" />
          Déconnexion
        </Button>
      </div>
    </aside>
  );
};

export const Layout = ({ children }) => {
  const currentYear = new Date().getFullYear();
  
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Sidebar />
      <SmtpHealthBanner />
      <main className="ml-64 flex-1 pb-12">
        {children}
      </main>
      {/* Footer - Editor banner */}
      <footer className="ml-64 fixed bottom-0 left-0 right-0 bg-slate-800 text-white py-2 px-4 z-40">
        <div className="flex items-center justify-between text-xs">
          <span>
            Système édité par{' '}
            <a 
              href="https://www.mpcsi.fr" 
              target="_blank" 
              rel="noopener noreferrer"
              className="font-semibold text-[#c5a059] hover:text-[#d4b87a] transition-colors"
            >
              MPCÂBLE Solutions Informatiques
            </a>
          </span>
          <span className="text-slate-400">
            © {currentYear} Diocèse de Pamiers - Tous droits réservés
          </span>
        </div>
      </footer>
    </div>
  );
};

export const PageHeader = ({ title, description, actions }) => {
  return (
    <header className="bg-white border-b border-slate-200 px-8 py-6" data-testid="page-header">
      <div className="flex items-center justify-between">
        <div>
          <h1 
            className="text-2xl font-bold text-[#1e3a8a]" 
            style={{ fontFamily: 'Merriweather, serif' }}
            data-testid="page-title"
          >
            {title}
          </h1>
          {description && (
            <p className="text-slate-600 mt-1">{description}</p>
          )}
        </div>
        {actions && <div className="flex gap-3">{actions}</div>}
      </div>
    </header>
  );
};
